import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Home, Users, Bed, AlertTriangle, FileText, ClipboardCheck, Eye } from 'lucide-react';

export default function AccommodationDashboardPage() {
  const [stats, setStats] = useState(null);
  const [occupancy, setOccupancy] = useState([]);
  const [vacancies, setVacancies] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    Promise.allSettled([
      api.get('/accommodation/dashboard'),
      api.get('/accommodation/occupancy'),
      api.get('/accommodation/vacancies'),
    ]).then(([dR, oR, vR]) => {
      if (dR.status === 'fulfilled') setStats(dR.value.data.data);
      if (oR.status === 'fulfilled') setOccupancy(oR.value.data.items || []);
      if (vR.status === 'fulfilled') setVacancies(vR.value.data.items || []);
    });
  }, []);

  const s = stats || {};

  return (
    <div className="space-y-6" data-testid="accommodation-dashboard">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2"><Home className="w-6 h-6" />Accommodation</h1>
          <p className="text-muted-foreground mt-1">Property and resident management</p>
        </div>
        <Button onClick={() => navigate('/accommodation/houses')} data-testid="view-properties-btn">
          <Eye className="w-4 h-4 mr-2" />View Properties
        </Button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="cursor-pointer hover:shadow-md" onClick={() => navigate('/accommodation/houses')}>
          <CardContent className="py-4 text-center">
            <Home className="w-5 h-5 mx-auto mb-1 text-primary" />
            <p className="text-2xl font-bold">{s.total_properties || 0}</p>
            <p className="text-xs text-muted-foreground">Properties ({s.active_properties || 0} active)</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="py-4 text-center">
            <Bed className="w-5 h-5 mx-auto mb-1 text-blue-600" />
            <p className="text-2xl font-bold">{s.total_rooms || 0}</p>
            <p className="text-xs text-muted-foreground">Rooms ({s.occupied_rooms || 0} occupied)</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="py-4 text-center">
            <Users className="w-5 h-5 mx-auto mb-1 text-green-600" />
            <p className="text-2xl font-bold">{s.total_residents || 0}</p>
            <p className="text-xs text-muted-foreground">Residents ({s.on_leave || 0} on leave)</p>
          </CardContent>
        </Card>
        <Card className={s.open_maintenance > 0 ? 'border-amber-200' : ''}>
          <CardContent className="py-4 text-center">
            <AlertTriangle className={`w-5 h-5 mx-auto mb-1 ${s.urgent_maintenance > 0 ? 'text-red-500' : 'text-amber-500'}`} />
            <p className="text-2xl font-bold">{s.open_maintenance || 0}</p>
            <p className="text-xs text-muted-foreground">Open Maintenance ({s.urgent_maintenance || 0} urgent)</p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="occupancy">
        <TabsList>
          <TabsTrigger value="occupancy" data-testid="tab-occupancy">Occupancy</TabsTrigger>
          <TabsTrigger value="vacancies" data-testid="tab-vacancies">Vacancies ({vacancies.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="occupancy" className="mt-4">
          {occupancy.length === 0 ? (
            <Card><CardContent className="py-8 text-center text-muted-foreground">No active properties</CardContent></Card>
          ) : (
            <div className="space-y-2">
              {occupancy.map(p => (
                <Card key={p.id} className="cursor-pointer hover:shadow-sm" onClick={() => navigate(`/houses/${p.id}`)} data-testid={`occupancy-${p.id}`}>
                  <CardContent className="py-3 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <Home className="w-5 h-5 text-muted-foreground" />
                      <div>
                        <p className="text-sm font-medium">{p.name}</p>
                        <p className="text-xs text-muted-foreground">{p.property_type || 'SIL'} · {p.branch_id || '-'}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 text-sm">
                      <span>{p.total_rooms} rooms</span>
                      <span className="font-medium">{p.occupied}/{p.capacity} occupied</span>
                      <div className="w-24 h-2 bg-muted rounded-full overflow-hidden">
                        <div className={`h-full rounded-full ${p.occupancy_pct >= 90 ? 'bg-red-500' : p.occupancy_pct >= 70 ? 'bg-amber-500' : 'bg-green-500'}`} style={{ width: `${Math.min(p.occupancy_pct, 100)}%` }} />
                      </div>
                      <span className="text-xs font-mono w-10 text-right">{p.occupancy_pct}%</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="vacancies" className="mt-4">
          {vacancies.length === 0 ? (
            <Card><CardContent className="py-8 text-center text-muted-foreground">No vacancies available</CardContent></Card>
          ) : (
            <div className="space-y-2">
              {vacancies.map((v, i) => (
                <Card key={i} data-testid={`vacancy-${i}`}>
                  <CardContent className="py-3 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <Bed className="w-5 h-5 text-green-600" />
                      <div>
                        <p className="text-sm font-medium">{v.property_name} — {v.room_name}</p>
                        <p className="text-xs text-muted-foreground">
                          {v.available_spots} spot(s) available
                          {v.available_beds?.length > 0 && ` · ${v.available_beds.length} bed(s)`}
                          {v.accessible && ' · Accessible'}
                        </p>
                      </div>
                    </div>
                    <Badge variant="default" className="bg-green-600">AVAILABLE</Badge>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
